/* Favourite locations — stored in localStorage so users can quickly switch
 * between their commute / home / weekend cities without re-searching.
 *
 * When the user is signed-in, the favourites list is also mirrored to the
 * backend (`saved_locations` collection) so the storm-watch sweeper can
 * fire push notifications across every pinned city — not just whatever
 * happens to be on screen.
 *
 * Schema: [{ id, name, admin1, admin2, country, latitude, longitude, pinned_at }]
 */
import { syncSavedLocations } from "./api";

const KEY = "nowcast.favourites.v1";
const MAX = 8;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    /* quota — ignore */
  }
}

function keyOf(loc) {
  if (!loc) return null;
  if (loc.id != null) return `id:${loc.id}`;
  const lat = Number(loc.latitude);
  const lon = Number(loc.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    return `coord:${lat.toFixed(3)}:${lon.toFixed(3)}`;
  }
  return null;
}

export function getFavourites() {
  return read();
}

export function isFavourite(loc) {
  const k = keyOf(loc);
  if (!k) return false;
  return read().some((f) => keyOf(f) === k);
}

export function toggleFavourite(loc) {
  const k = keyOf(loc);
  if (!k) return read();
  const list = read();
  const idx = list.findIndex((f) => keyOf(f) === k);
  if (idx >= 0) {
    list.splice(idx, 1);
  } else {
    list.unshift({
      id: loc.id ?? null,
      name: loc.name,
      admin1: loc.admin1 || null,
      admin2: loc.admin2 || null,
      country: loc.country || null,
      latitude: Number(loc.latitude),
      longitude: Number(loc.longitude),
      timezone: loc.timezone || null,
      pinned_at: Date.now(),
    });
  }
  write(list);
  // Tell other tabs / components in the same tab.
  try {
    window.dispatchEvent(new CustomEvent("nowcast:favourites-changed"));
  } catch {
    /* SSR / older browsers */
  }
  // Mirror to backend for signed-in users (fire-and-forget).
  pushFavouritesToBackend(read());
  return read();
}

let _backendSyncTimer = null;
let _backendSyncEnabled = true;

export function setBackendSyncEnabled(on) {
  _backendSyncEnabled = !!on;
  if (on) pushFavouritesToBackend(read());
}

export function pushFavouritesToBackend(list) {
  if (!_backendSyncEnabled) return;
  // Debounce — multiple pin/unpin actions in quick succession get coalesced
  // into one network call.
  if (_backendSyncTimer) clearTimeout(_backendSyncTimer);
  _backendSyncTimer = setTimeout(() => {
    syncSavedLocations(
      (list || []).map((f) => ({
        location_id: f.id != null ? `om-${f.id}` : undefined,
        name: f.name,
        admin1: f.admin1 || null,
        admin2: f.admin2 || null,
        country: f.country || null,
        latitude: Number(f.latitude),
        longitude: Number(f.longitude),
        timezone: f.timezone || null,
        alerts_enabled: true,
      })),
    ).catch(() => {
      /* user not signed-in or offline — ignore, the localStorage list is
         the source of truth and we'll re-sync on the next change. */
    });
  }, 600);
}

export function FAVOURITES_EVENT() {
  return "nowcast:favourites-changed";
}
